import { TEMI } from '@mastro/core/tipi';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type Categoria, type Organizzazione, type Tema } from '../api';
import { Campo, CampoImporto, Scheda, useAvvisi, useCarico } from '../componenti/base';
import { useSessione } from '../main';

// Etichetta e pastiglia di colore per ogni skin (stile AdminLTE 2).
const TINTE: Record<Tema, { nome: string; colore: string }> = {
  blu: { nome: 'Blu', colore: '#3c8dbc' },
  verde: { nome: 'Verde', colore: '#00a65a' },
  viola: { nome: 'Viola', colore: '#605ca8' },
  rosso: { nome: 'Rosso', colore: '#dd4b39' },
  nero: { nome: 'Ardesia', colore: '#2c3b41' },
};

export function Impostazioni() {
  // Serve solo alla scelta della voce IVA: poche righe, si caricano sempre.
  const categorie = useCarico(() => api.get<Categoria[]>('/api/categorie'), []);
  const { organizzazione, ricarica } = useSessione();
  const avvisa = useAvvisi();
  const [v, setV] = useState<Organizzazione>(organizzazione);
  const [attesa, setAttesa] = useState(false);

  const salva = async (e: React.FormEvent) => {
    e.preventDefault();
    setAttesa(true);
    try {
      await api.put('/api/organizzazione', {
        ragioneSociale: v.ragioneSociale,
        partitaIva: v.partitaIva || null,
        codiceFiscale: v.codiceFiscale || null,
        indirizzo: v.indirizzo || null,
        cap: v.cap || null,
        comune: v.comune || null,
        provincia: v.provincia || null,
        codiceDestinatario: v.codiceDestinatario || null,
        pec: v.pec || null,
        sogliaMinimaCent: v.sogliaMinimaCent,
        suggerisciRicorrenti: v.suggerisciRicorrenti,
        formatoData: v.formatoData,
        primoMeseEsercizio: v.primoMeseEsercizio,
        aliquotaIvaDefault: v.aliquotaIvaDefault,
        regimeIva: v.regimeIva,
        interessiIvaPercentuale: v.interessiIvaPercentuale,
        creditoIvaInizialeCent: v.creditoIvaInizialeCent,
        categoriaIvaId: v.categoriaIvaId,
        categoriaDefaultAttiveId: v.categoriaDefaultAttiveId,
        categoriaDefaultPassiveId: v.categoriaDefaultPassiveId,
      });
      avvisa('Impostazioni salvate.');
      ricarica();
    } catch (err) {
      avvisa(err instanceof Error ? err.message : 'Salvataggio non riuscito.', 'errore');
    } finally {
      setAttesa(false);
    }
  };

  return (
    <div className="pagina space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold tracking-tight">Impostazioni</h1>
          <p className="text-sm text-inchiostro-500">Dati dell’azienda e preferenze di visualizzazione.</p>
        </div>
        <Link to="/impostazioni/integrazioni" className="bottone-secondario">
          Integrazioni
        </Link>
      </header>

      <form onSubmit={salva} className="space-y-5">
        <Scheda
          titolo="Logo"
          sottotitolo="Compare in alto a sinistra, al posto del nome. Serve a riconoscere a colpo d’occhio di quale azienda sono i numeri che stai guardando."
        >
          <CampoLogo
            presente={organizzazione.logoPresente}
            aggiornatoIl={organizzazione.logoAggiornatoIl}
            onCambiato={ricarica}
          />
        </Scheda>

        <Scheda titolo="Azienda" sottotitolo="La partita IVA serve a riconoscere il verso delle fatture elettroniche importate.">
          <div className="grid gap-3 sm:grid-cols-2">
            <Campo etichetta="Ragione sociale" larghezza="sm:col-span-2">
              <input className="campo" required value={v.ragioneSociale} onChange={(e) => setV({ ...v, ragioneSociale: e.target.value })} />
            </Campo>
            <Campo etichetta="Partita IVA">
              <input className="campo num" value={v.partitaIva ?? ''} onChange={(e) => setV({ ...v, partitaIva: e.target.value })} />
            </Campo>
            <Campo etichetta="Codice fiscale">
              <input className="campo num" value={v.codiceFiscale ?? ''} onChange={(e) => setV({ ...v, codiceFiscale: e.target.value })} />
            </Campo>
            <Campo etichetta="Indirizzo" larghezza="sm:col-span-2">
              <input className="campo" value={v.indirizzo ?? ''} onChange={(e) => setV({ ...v, indirizzo: e.target.value })} />
            </Campo>
            <Campo etichetta="CAP">
              <input className="campo" value={v.cap ?? ''} onChange={(e) => setV({ ...v, cap: e.target.value })} />
            </Campo>
            <Campo etichetta="Comune">
              <input className="campo" value={v.comune ?? ''} onChange={(e) => setV({ ...v, comune: e.target.value })} />
            </Campo>
            <Campo etichetta="Provincia">
              <input className="campo" maxLength={4} value={v.provincia ?? ''} onChange={(e) => setV({ ...v, provincia: e.target.value })} />
            </Campo>
            <Campo etichetta="Codice destinatario SDI">
              <input className="campo num" maxLength={10} value={v.codiceDestinatario ?? ''} onChange={(e) => setV({ ...v, codiceDestinatario: e.target.value })} />
            </Campo>
            <Campo etichetta="PEC" larghezza="sm:col-span-2">
              <input className="campo" value={v.pec ?? ''} onChange={(e) => setV({ ...v, pec: e.target.value })} />
            </Campo>
          </div>
        </Scheda>

        <Scheda titolo="Tesoreria">
          <div className="grid gap-3 sm:grid-cols-2">
            <Campo
              etichetta="Soglia minima di cassa"
              aiuto="Sotto questa soglia il previsionale va in allerta: è il margine di sicurezza che ti dai tu. Il fido della banca non va qui — si indica sul singolo conto, in Conti e movimenti, perché è un fatto di quel rapporto e non una tua scelta."
            >
              <CampoImporto valoreCent={v.sogliaMinimaCent} onCambia={(c) => setV({ ...v, sogliaMinimaCent: c })} />
            </Campo>
            <Campo etichetta="Aliquota IVA predefinita">
              <input
                className="campo num text-right"
                value={v.aliquotaIvaDefault}
                onChange={(e) => setV({ ...v, aliquotaIvaDefault: e.target.value })}
              />
            </Campo>
          </div>
          <label className="mt-4 flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-1"
              checked={v.suggerisciRicorrenti}
              onChange={(e) => setV({ ...v, suggerisciRicorrenti: e.target.checked })}
            />
            <span>
              <strong>Suggerisci voci di budget dai fornitori e clienti che si ripetono</strong>
              <span className="block text-xs text-inchiostro-500">
                Nella pagina Budget compare l’elenco di chi ha fatturato quasi ogni mese negli ultimi sei, con la voce
                che lo rappresenterebbe. Sono solo suggerimenti: niente viene scelto o creato senza di te.
              </span>
            </span>
          </label>
        </Scheda>

        <Scheda
          titolo="Documenti"
          sottotitolo="Quello che Mastro assume quando importi una fattura e nessuno ha detto niente."
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <Campo
              etichetta="Voce predefinita delle fatture attive"
              aiuto="Vale quando né il documento né il cliente hanno già una voce. I ricavi di solito stanno sempre sulla stessa: assegnarla a mano una per una è lavoro che non decide niente."
            >
              <select
                className="campo"
                value={v.categoriaDefaultAttiveId ?? ''}
                onChange={(e) => setV({ ...v, categoriaDefaultAttiveId: e.target.value || null })}
              >
                <option value="">Nessuna: restano da identificare</option>
                {(categorie.dati ?? [])
                  .filter((c) => c.verso === 'ENTRATA' && !c.sistema)
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nome}
                    </option>
                  ))}
              </select>
            </Campo>

            <Campo
              etichetta="Voce predefinita delle fatture di acquisto"
              aiuto="Stessa regola. Sui fornitori la voce giusta cambia spesso: quella impostata sul singolo fornitore in Anagrafiche vince comunque su questa, ed è lì che conviene sistemarli uno alla volta."
            >
              <select
                className="campo"
                value={v.categoriaDefaultPassiveId ?? ''}
                onChange={(e) => setV({ ...v, categoriaDefaultPassiveId: e.target.value || null })}
              >
                <option value="">Nessuna: restano da identificare</option>
                {(categorie.dati ?? [])
                  .filter((c) => c.verso === 'USCITA' && !c.sistema)
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nome}
                    </option>
                  ))}
              </select>
            </Campo>
          </div>
        </Scheda>

        <Scheda
          titolo="IVA"
          sottotitolo="Serve al previsionale per sapere quando l’imposta esce dal conto: è una delle uscite più grosse e più dimenticate."
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <Campo
              etichetta="Liquidazione"
              aiuto="Mensile è il regime ordinario: si versa entro il 16 del mese dopo. Il trimestrale è un’opzione: 16 maggio, 16 agosto, 16 novembre e il quarto trimestre col saldo annuale a marzo."
            >
              <select
                className="campo"
                value={v.regimeIva}
                onChange={(e) => setV({ ...v, regimeIva: e.target.value as 'MENSILE' | 'TRIMESTRALE' })}
              >
                <option value="MENSILE">Mensile</option>
                <option value="TRIMESTRALE">Trimestrale</option>
              </select>
            </Campo>
            {v.regimeIva === 'TRIMESTRALE' && (
              <Campo
                etichetta="Interessi sui versamenti trimestrali (%)"
                aiuto="L’1% lo pagano i trimestrali per opzione. I trimestrali «naturali» no: in quel caso metti 0."
              >
                <input
                  className="campo num text-right"
                  value={v.interessiIvaPercentuale}
                  onChange={(e) => setV({ ...v, interessiIvaPercentuale: e.target.value })}
                />
              </Campo>
            )}
            <Campo
              etichetta="Voce del previsionale"
              aiuto="Sotto quale voce mostrare i versamenti IVA. Lasciandola vuota Mastro cerca «IVA», poi «Imposte e tributi»."
            >
              <select
                className="campo"
                value={v.categoriaIvaId ?? ''}
                onChange={(e) => setV({ ...v, categoriaIvaId: e.target.value || null })}
              >
                <option value="">La cerca da sé</option>
                {(categorie.dati ?? [])
                  .filter((c) => c.verso === 'USCITA' && !c.sistema)
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nome}
                    </option>
                  ))}
              </select>
            </Campo>

            <Campo
              etichetta="Credito IVA dall’anno precedente"
              aiuto="Se te lo porti dietro, riduce le prime liquidazioni previste. Senza, il previsionale le mostrerebbe più care del vero."
            >
              <CampoImporto
                valoreCent={v.creditoIvaInizialeCent}
                onCambia={(c) => setV({ ...v, creditoIvaInizialeCent: c })}
              />
            </Campo>
          </div>
        </Scheda>

        <Scheda titolo="Visualizzazione">
          <div className="grid gap-3 sm:grid-cols-2">
            <Campo
              etichetta="Formato data"
              aiuto="È una preferenza tua, non si deduce dalla lingua del browser."
            >
              <select
                className="campo"
                value={v.formatoData}
                onChange={(e) => setV({ ...v, formatoData: e.target.value as Organizzazione['formatoData'] })}
              >
                <option value="gg/mm/aaaa">31/12/2026</option>
                <option value="gg-mm-aaaa">31-12-2026</option>
                <option value="aaaa-mm-gg">2026-12-31</option>
              </select>
            </Campo>
            <Campo etichetta="Primo mese dell’esercizio">
              <select
                className="campo"
                value={v.primoMeseEsercizio}
                onChange={(e) => setV({ ...v, primoMeseEsercizio: Number(e.target.value) })}
              >
                {['gennaio','febbraio','marzo','aprile','maggio','giugno','luglio','agosto','settembre','ottobre','novembre','dicembre'].map((m, i) => (
                  <option key={m} value={i + 1}>{m}</option>
                ))}
              </select>
            </Campo>
          </div>
        </Scheda>

        <div className="flex justify-end">
          <button type="submit" className="bottone-primario" disabled={attesa}>
            {attesa ? 'Salvataggio…' : 'Salva impostazioni'}
          </button>
        </div>
      </form>

      <PreferenzePersonali />
    </div>
  );
}

/**
 * Preferenze **personali**: valgono per l'utente, non per l'azienda, e si
 * salvano su un endpoint diverso da quello dell'organizzazione. La skin si
 * applica subito (anteprima) e, se il salvataggio fallisce, si torna indietro.
 */
function PreferenzePersonali() {
  const { utente, ricarica } = useSessione();
  const avvisa = useAvvisi();
  const [tema, setTema] = useState<Tema>(utente.tema);
  const [attesa, setAttesa] = useState(false);

  const scegli = async (nuovo: Tema) => {
    if (nuovo === tema) return;
    const precedente = tema;
    setTema(nuovo);
    document.documentElement.setAttribute('data-skin', nuovo);
    setAttesa(true);
    try {
      await api.put('/api/io/preferenze', { tema: nuovo });
      ricarica();
      avvisa('Tema aggiornato.');
    } catch (err) {
      setTema(precedente);
      document.documentElement.setAttribute('data-skin', precedente);
      avvisa(err instanceof Error ? err.message : 'Non è stato possibile cambiare tema.', 'errore');
    } finally {
      setAttesa(false);
    }
  };

  return (
    <Scheda titolo="Preferenze personali" sottotitolo="Valgono solo per te, non per gli altri utenti dell’azienda.">
      {/* Niente <label> qui: i pulsanti non vanno avvolti in un label, o un clic
          attiverebbe anche il primo controllo. Etichetta e aiuto sono testo. */}
      <p className="etichetta">Tema dell’interfaccia</p>
      <div className="flex flex-wrap gap-2" role="group" aria-label="Tema dell’interfaccia">
        {TEMI.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => scegli(t)}
            disabled={attesa}
            aria-pressed={tema === t}
            className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors disabled:opacity-60 ${
              tema === t ? 'border-skin ring-2 ring-skin/30' : 'border-inchiostro-300 hover:bg-inchiostro-100'
            }`}
          >
            <span className="h-4 w-4 rounded-full ring-1 ring-black/10" style={{ backgroundColor: TINTE[t].colore }} />
            {TINTE[t].nome}
          </button>
        ))}
      </div>
      <p className="mt-2 text-xs text-inchiostro-500">
        Cambia solo i colori della cornice (barra e menu). Le tinte dei numeri restano quelle.
      </p>
    </Scheda>
  );
}

/**
 * Caricamento del logo.
 *
 * L'immagine non passa dal modulo delle impostazioni: si carica appena scelta e
 * si vede subito. Un logo caricato ma «da salvare» insieme alla partita IVA
 * sarebbe una trappola — si sceglie il file, si va via, e non è successo niente.
 *
 * L'anteprima porta in coda la data di aggiornamento: senza, dopo una
 * sostituzione il browser continuerebbe a mostrare quello vecchio, che è già in
 * cache con lo stesso indirizzo.
 */
function CampoLogo({
  presente,
  aggiornatoIl,
  onCambiato,
}: {
  presente: boolean;
  aggiornatoIl: string | null;
  onCambiato: () => void;
}) {
  const avvisa = useAvvisi();
  const scelta = useRef<HTMLInputElement>(null);
  const [attesa, setAttesa] = useState(false);

  const carica = async (file: File) => {
    setAttesa(true);
    try {
      const modulo = new FormData();
      modulo.append('file', file);
      await api.carica('/api/organizzazione/logo', modulo);
      avvisa('Logo aggiornato.');
      onCambiato();
    } catch (e) {
      avvisa(e instanceof Error ? e.message : 'Caricamento non riuscito.', 'errore');
    } finally {
      setAttesa(false);
      if (scelta.current) scelta.current.value = '';
    }
  };

  const rimuovi = async () => {
    if (!confirm('Togliere il logo? In alto tornerà il nome dell’azienda.')) return;
    try {
      await api.del('/api/organizzazione/logo');
      avvisa('Logo rimosso.');
      onCambiato();
    } catch (e) {
      avvisa(e instanceof Error ? e.message : 'Rimozione non riuscita.', 'errore');
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-4">
      <div className="grid h-20 w-40 shrink-0 place-items-center rounded-md border border-inchiostro-200 bg-inchiostro-100 p-2">
        {presente ? (
          <img
            src={`/api/organizzazione/logo?v=${encodeURIComponent(aggiornatoIl ?? '')}`}
            alt="Logo dell’azienda"
            className="max-h-full max-w-full object-contain"
          />
        ) : (
          <span className="text-xs text-inchiostro-400">nessun logo</span>
        )}
      </div>

      <div className="min-w-0">
        {/* `type="button"` non è pignoleria: questa scheda vive **dentro** il
            modulo delle impostazioni, e un bottone senza tipo dichiarato in un
            form vale come invio. Senza, «Scegli il file» salvava le
            impostazioni invece di aprire la finestra di scelta — e chi ci
            provava leggeva «Impostazioni salvate», che è la risposta giusta
            alla domanda sbagliata. */}
        <div className="flex flex-wrap gap-2">
          <button type="button" className="bottone-secondario" disabled={attesa} onClick={() => scelta.current?.click()}>
            {attesa ? 'Caricamento…' : presente ? 'Sostituisci' : 'Scegli il file'}
          </button>
          {presente && (
            <button type="button" className="bottone-secondario" onClick={rimuovi}>
              Togli
            </button>
          )}
        </div>
        <input
          ref={scelta}
          type="file"
          hidden
          /**
           * `image/*` e non l'elenco dei formati ammessi: un filtro stretto fa
           * sì che il file non si possa nemmeno **selezionare**, e chi ci prova
           * vede solo una finestra che ignora il suo logo — senza sapere
           * perché. Meglio lasciarlo scegliere e far spiegare al server cosa
           * non va: il messaggio dice quali formati accetta.
           */
          accept="image/*"
          onChange={(e) => e.target.files?.[0] && carica(e.target.files[0])}
        />
        <p className="mt-2 max-w-md text-xs text-inchiostro-500">
          PNG, JPEG, WebP o SVG, fino a 1 MB. Viene mostrato alto 28 pixel: un’immagine larga si legge meglio di una
          quadrata, e su fondo scuro conviene un logo chiaro o con lo sfondo trasparente.
        </p>
      </div>
    </div>
  );
}
